import React, { useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { useGetIpJsonQuery } from "../../api/thirdPartyApiSlice";
import { useLoginMutation, useVerify2FAMutation, useCheck2FAStatusQuery, useGetUserQuery } from "../../api/apiSlice";
import { useAuth } from "../../hooks";
import { getCachedIpInfo, setCachedIpInfo } from "../../utils/ipInfoCache";
import { TwoFactorAuth, TwoFactorPrompt, TwoFactorSetup } from "../../components";
import { getApiErrorMessage } from "../../utils";
import { getDefaultRoute } from "../../utils/permissions";
import { UserRole } from "../../utils/types";

interface RegisterState {
  email: string;
  password: string;
}

type LoginStep = "login" | "2fa" | "prompt-2fa" | "setup-2fa";

const Login: React.FC = () => {
  const { setLocalStorage, removeLocalStorage } = useAuth();

  const navigate = useNavigate();

  const [step, setStep] = useState<LoginStep>("login");
  const [tempToken, setTempToken] = useState("");
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showSetupSuccess, setShowSetupSuccess] = useState(false);

  const [loginData, setLoginData] = useState<RegisterState>({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<RegisterState>({
    email: "",
    password: "",
  });

  const [apiError, setApiError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData({
      ...loginData,
      [name]: value,
    });
    setErrors(prev => ({ ...prev, [name]: "" }));
    setApiError("");
  };

  // Retrieve cached IP data if available
  const cachedIpData = useMemo(() => getCachedIpInfo(), []);
  const shouldFetchIpData = !cachedIpData;

  // NOTE: The ipData could be undefined if the request to fetch IP information fails.
  // This scenario is handled in the api slice method to ensure that the application
  // does not break if ipData is not available. The login mutation in the api slice
  // checks for the presence of ipData before attempting to access its properties.
  const { data: ipData } = useGetIpJsonQuery({}, { skip: !shouldFetchIpData });

  const [login, loginResult] = useLoginMutation();
  const [verify2FA, verify2FAResult] = useVerify2FAMutation();
  const [checkAfterLogin, setCheckAfterLogin] = useState(false);
  const [resolvePostLoginRoute, setResolvePostLoginRoute] = useState(false);

  const { data: twoFactorStatus } = useCheck2FAStatusQuery(undefined, { skip: !checkAfterLogin });
  const { data: userData } = useGetUserQuery({}, { skip: !resolvePostLoginRoute });

  const { isSuccess, isLoading } = loginResult;
  const { isLoading: isVerifying } = verify2FAResult;

  useEffect(() => {
    if (resolvePostLoginRoute && userData) {
      const userRoles = userData?.roles?.map((role: UserRole) => role.role) || [];
      const defaultRoute = getDefaultRoute(userRoles);
      navigate(defaultRoute, { replace: true });
      setResolvePostLoginRoute(false);
    }
  }, [resolvePostLoginRoute, userData, navigate]);

  useEffect(() => {
    // Cache the IP data if it is fetched
    if (ipData) {
      setCachedIpInfo(ipData);
    }
  }, [ipData, cachedIpData]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");

    const newErrors = { email: "", password: "" };

    if (!loginData.email.trim()) {
      newErrors.email = "Email is required";
    }

    if (!loginData.password) {
      newErrors.password = "Password is required";
    }

    if (newErrors.email || newErrors.password) {
      setErrors(newErrors);
      return;
    }

    login({ email: loginData.email.trim(), password: loginData.password, ipData: ipData || cachedIpData })
      .unwrap()
      .then(response => {
        const data = (response as any)?.data || response;

        if (data.requiresTwoFactor) {
          // User has 2FA enabled - proceed to verification
          setTempToken(data.tempToken || "");
          setStep("2fa");
        } else {
          // User doesn't have 2FA - temporarily store token and check status
          // This token is temporary and will be replaced after 2FA setup
          setLocalStorage(data);
          setCheckAfterLogin(true);
        }
      })
      .catch((error: any) => {
        const errorMessage = getApiErrorMessage(error, "Unable to sign in. Please try again.");
        setApiError(errorMessage);
      });
  };

  const handle2FAVerification = async ({ tempToken: token, code }: { tempToken: string; code: string }) => {
    setApiError("");
    try {
      const response = await verify2FA({ tempToken: token, code }).unwrap();
      // Handle different response formats from backend (similar to error handling)
      // Some backends might return nested data, others might return direct format
      const authData = (response as any)?.data || response;
      setLocalStorage(authData);
      setResolvePostLoginRoute(true);
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error, "Unable to verify. Please try again.");
      setApiError(errorMessage);
    }
  };

  const handleBack = () => {
    setStep("login");
    setTempToken("");
    setErrors({ email: "", password: "" });
    setApiError("");
  };

  const handlePromptEnable2FA = () => {
    setShowSetupDialog(true);
  };

  const handleSkip2FA = () => {
    // Clear temporary token and force logout since 2FA is now mandatory
    removeLocalStorage();
    setStep("login");
    setApiError("Two-Factor Authentication is required for security. Please enable 2FA to continue.");
  };

  const handleSetupComplete = () => {
    setShowSetupDialog(false);
    // After 2FA setup, clear temporary tokens and show success state
    removeLocalStorage();
    setShowSetupSuccess(true);
    setStep("login");
    setErrors({
      email: "",
      password: "",
    });
    // Pre-fill the email for convenience
    // Note: loginData.email should still have the email from previous attempt
  };

  useEffect(() => {
    if (isSuccess && loginResult?.data?.accessToken) {
      if (step === "2fa") {
        setResolvePostLoginRoute(true);
      }
    }
  }, [isSuccess, loginResult, step]);

  // Handle 2FA status check after login
  useEffect(() => {
    if (twoFactorStatus && checkAfterLogin) {
      if (!twoFactorStatus.enabled) {
        // 2FA is mandatory - prompt user to enable it
        setStep("prompt-2fa");
      } else {
        // User already has 2FA enabled but somehow got regular login
        // This shouldn't happen in normal flow, but handle gracefully
        setResolvePostLoginRoute(true);
      }
      setCheckAfterLogin(false);
    }
  }, [twoFactorStatus, checkAfterLogin]);

  useEffect(() => {
    removeLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <Box sx={{ pb: 4 }}>
      {showSetupSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setShowSetupSuccess(false)}>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
            2FA Enabled Successfully!
          </Typography>
          <Typography variant="body2">Please log in again with your email, password, and authenticator code.</Typography>
        </Alert>
      )}

      {step === "login" && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 5 }}>
            Login
          </Typography>
          <form onSubmit={onSubmit}>
            {apiError && (
              <Alert
                severity="error"
                variant="outlined"
                sx={{
                  mb: 2,
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                  borderRadius: 1,
                  "& .MuiAlert-icon": { alignItems: "center" },
                }}
                onClose={() => setApiError("")}
              >
                {apiError}
              </Alert>
            )}
            <TextField
              label="Email"
              variant="standard"
              name="email"
              value={loginData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Password"
              type="password"
              variant="standard"
              name="password"
              error={!!errors.password}
              helperText={errors.password}
              value={loginData.password}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <Button
              sx={{ mt: 8, width: "100%", textTransform: "none" }}
              disableElevation
              disabled={isLoading}
              variant="contained"
              color="primary"
              size="large"
              type="submit"
            >
              {isLoading ? <CircularProgress size={30} /> : `Sign In`}
            </Button>
          </form>
        </>
      )}

      {step === "2fa" && (
        <TwoFactorAuth
          tempToken={tempToken}
          onVerificationSuccess={handle2FAVerification}
          onBack={handleBack}
          isLoading={isVerifying}
          error={apiError}
        />
      )}

      {step === "prompt-2fa" && <TwoFactorPrompt onEnable2FA={handlePromptEnable2FA} onSkip={handleSkip2FA} />}

      {showSetupDialog && (
        <TwoFactorSetup open={showSetupDialog} onClose={() => setShowSetupDialog(false)} onSetupComplete={handleSetupComplete} />
      )}
    </Box>
  );
};

export default Login;
